import React from "react";
import SectionShell from "../../shared/SectionShell";

const documentCardStyle = {
  border: "1px solid var(--border, #e5e5e5)",
  borderRadius: 12,
  padding: 12,
  background: "#fafafa",
  display: "grid",
  gap: 8,
};

const twoColumnGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: 16,
};

const StudentHealthTab = ({
  formData,
  handleChange,
  cardStyle,
  labelStyle,
  inputStyle,
  textareaStyle,
  chipStyle,
  dateInputStyle,
  miniActionBtnStyle,
  getDocBadgeStyle,
  ALLERGY_OPTIONS,
  SUPPORT_TAG_OPTIONS,
  REQUIRED_HEALTH_DOCUMENTS,
  handleAllergyToggle,
  handleSupportTagToggle,
  updateRequiredHealthDocumentField,
  uploadRequiredHealthDocument,
  removeRequiredHealthDocument,
  handleHealthDocumentUpload,
  removeHealthDocument,
  openDocument,
  downloadDocument,
  printDocument,
}) => {
  return (
    <SectionShell
      title="Health & Safety"
      subtitle="Allergies, medications, support needs, and required health documentation."
    >
      <div style={twoColumnGrid}>
        <div style={cardStyle}>
          <div style={labelStyle}>Allergies</div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {ALLERGY_OPTIONS.map((allergy) => (
              <button
                key={allergy}
                type="button"
                style={chipStyle((formData.allergySelections || []).includes(allergy))}
                onClick={() => handleAllergyToggle(allergy)}
              >
                {allergy}
              </button>
            ))}
          </div>

          <div style={{ marginTop: 8 }}>
            <div style={labelStyle}>Other Allergies</div>
            <input
              style={inputStyle}
              value={formData.allergyOther || ""}
              onChange={(e) => handleChange("allergyOther", e.target.value)}
              placeholder="Add other allergies separated by commas"
            />
          </div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Medication Allergies</div>
          <textarea
            style={textareaStyle}
            value={formData.medicationAllergies || ""}
            onChange={(e) => handleChange("medicationAllergies", e.target.value)}
            placeholder="List medication allergies such as penicillin, amoxicillin, etc."
          />
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Current Medications</div>
          <textarea
            style={textareaStyle}
            value={formData.medications || ""}
            onChange={(e) => handleChange("medications", e.target.value)}
            placeholder="List medications currently taken at school or home"
          />
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Disability / Support Tags</div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {SUPPORT_TAG_OPTIONS.map((tag) => (
              <button
                key={tag}
                type="button"
                style={chipStyle((formData.disabilitySupportTags || []).includes(tag))}
                onClick={() => handleSupportTagToggle(tag)}
              >
                {tag}
              </button>
            ))}
          </div>

          <div style={{ marginTop: 8 }}>
            <div style={labelStyle}>Other Support Needs</div>
            <input
              style={inputStyle}
              value={formData.supportTagsOther || ""}
              onChange={(e) => handleChange("supportTagsOther", e.target.value)}
              placeholder="Add support needs not listed above"
            />
          </div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Health Notes</div>
          <textarea
            style={textareaStyle}
            value={formData.healthRecords || ""}
            onChange={(e) => handleChange("healthRecords", e.target.value)}
            placeholder="Add health notes, immunization notes, care plans, restrictions, or other health-related information"
          />
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <div style={cardStyle}>
            <div style={labelStyle}>Required Health Documents</div>

            <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
              {REQUIRED_HEALTH_DOCUMENTS.map((requiredDoc) => {
                const doc = formData.healthRequiredDocuments?.[requiredDoc.key];

                return (
                  <div key={requiredDoc.key} style={documentCardStyle}>
                    <div style={{ fontWeight: 800 }}>{requiredDoc.title}</div>

                    <div style={{ color: "var(--text-secondary, #666)", fontSize: ".92rem" }}>
                      {doc?.title || doc?.name || "No file uploaded"}
                    </div>

                    <div style={getDocBadgeStyle(doc?.status || "Missing")}>
                      {doc?.status || "Missing"}
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={labelStyle}>Due Date</div>

                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 16 }}>📅</span>
                        <input
                          type="date"
                          style={dateInputStyle}
                          value={doc?.due || ""}
                          onChange={(e) =>
                            updateRequiredHealthDocumentField(
                              requiredDoc.key,
                              "due",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>

                    <div style={{ color: "var(--text-secondary, #666)" }}>
                      Uploaded:{" "}
                      {doc?.uploadedAt
                        ? new Date(doc.uploadedAt).toLocaleDateString()
                        : "Not uploaded"}
                    </div>

                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 14px",
                        borderRadius: 12,
                        border: "1px dashed var(--border, #ccc)",
                        background: "#fff",
                        cursor: "pointer",
                        fontWeight: 700,
                        width: "fit-content",
                      }}
                    >
                      Upload {requiredDoc.title}
                      <input
                        type="file"
                        hidden
                        onChange={(e) =>
                          uploadRequiredHealthDocument(
                            requiredDoc.key,
                            e.target.files?.[0]
                          )
                        }
                      />
                    </label>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={miniActionBtnStyle}
                        onClick={() => openDocument(doc?.fileUrl)}
                      >
                        View
                      </button>

                      <button
                        type="button"
                        style={miniActionBtnStyle}
                        onClick={() =>
                          downloadDocument(doc?.fileUrl, doc?.title || requiredDoc.title)
                        }
                      >
                        Download
                      </button>

                      <button
                        type="button"
                        style={miniActionBtnStyle}
                        onClick={() => printDocument(doc?.fileUrl)}
                      >
                        Print
                      </button>

                      <button
                        type="button"
                        style={miniActionBtnStyle}
                        onClick={() => removeRequiredHealthDocument(requiredDoc.key)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <div style={cardStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={labelStyle}>Other Health Documents</div>

              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: 12,
                  border: "1px dashed var(--border, #ccc)",
                  background: "#fafafa",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Upload Health Document
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => handleHealthDocumentUpload(e.target.files)}
                />
              </label>
            </div>

            {(formData.healthDocuments || []).length ? (
              <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                {formData.healthDocuments.map((doc) => (
                  <div key={doc._id} style={documentCardStyle}>
                    <div style={{ fontWeight: 800 }}>
                      {doc.title || doc.name || "Untitled Document"}
                    </div>

                    <div style={{ color: "var(--text-secondary, #666)", fontSize: ".92rem" }}>
                      File: {doc?.title || doc?.name || "No file uploaded"}
                    </div>

                    <div style={{ color: "var(--text-secondary, #666)" }}>
                      Uploaded:{" "}
                      {doc.uploadedAt
                        ? new Date(doc.uploadedAt).toLocaleDateString()
                        : "New"}
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={miniActionBtnStyle}
                        onClick={() => openDocument(doc.fileUrl)}
                      >
                        View
                      </button>

                      <button
                        type="button"
                        style={miniActionBtnStyle}
                        onClick={() => downloadDocument(doc.fileUrl, doc.name)}
                      >
                        Download
                      </button>

                      <button
                        type="button"
                        style={miniActionBtnStyle}
                        onClick={() => printDocument(doc.fileUrl)}
                      >
                        Print
                      </button>

                      <button
                        type="button"
                        style={miniActionBtnStyle}
                        onClick={() => removeHealthDocument(doc._id)}
                      >
                        Remove Document
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "var(--text-secondary, #666)", marginTop: 12 }}>
                No other health documents uploaded yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </SectionShell>
  );
};

export default StudentHealthTab;